import { PORTAL_TAB_LABELS, type PortalViewOutput } from '@garage/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { DESKTOP_QUERY, useMediaQuery } from '../../shared/layout.js';
import { BrandMark } from '../../shared/components/BrandMark.js';
import { cx } from '../../ui/cx.js';
import { CarIcon, ClipboardIcon, MessageIcon, WrenchIcon } from '../../ui/icons.js';
import { useInputMode } from '../../ui/useInputMode.js';
import { api } from '../../shared/api.js';
import { PORTAL_QUERY_KEY } from '../keys.js';
import { PortalNavContext, latestMessageId, readStoredTab, storeTab, tabsFor, type PortalNav, type PortalTab } from '../nav.js';
import { usePortalPreview } from '../preview.js';
import { TimezoneContext } from '../timezone.js';
import { MessagesTab } from './MessagesTab.js';
import { ReceptionTab } from './ReceptionTab.js';
import { SummaryTab } from './SummaryTab.js';
import { WorkTab } from './WorkTab.js';
import { BrandHeader, PrivacyFooter, VehicleText } from './portal-ui.js';

const TAB_ICONS: Record<PortalTab, typeof CarIcon> = { summary: CarIcon, reception: ClipboardIcon, work: WrenchIcon, messages: MessageIcon };

/** Fait défiler jusqu'à un bloc (et ouvre la saisie d'un message), sinon remonte en haut de l'onglet. */
function reveal(target: string | null): void {
  if (target === 'ecrire') {
    const field = document.querySelector<HTMLTextAreaElement>('#echanges textarea');
    field?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    field?.focus({ preventScroll: true });
    return;
  }
  if (target) {
    document.getElementById(target)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    return;
  }
  try {
    window.scrollTo({ top: 0 });
  } catch {
    // environnement sans défilement (tests)
  }
}

/**
 * Page du client, ouverte par le lien unique de l'e-mail : **uniquement l'intervention en cours** (CONTRACT
 * §18), à la marque du garage (trois modes de présence : couleurs de `applyBrand`, habillage `chrome` et
 * `masthead`), en onglets pour ne pas surcharger l'écran :
 * - téléphone : en-tête à la marque, barre d'onglets en bas (au pouce), masquée pendant la saisie ;
 * - ordinateur : menu à gauche aux couleurs du garage, contenu à droite ;
 * - aperçu côté garage (« Voir ce que voit mon client », cadre inerte) : onglets dépliés l'un sous l'autre.
 * Lien de suivi (`active`) ou compte rendu permanent (`report`, lecture seule, sans messagerie).
 * Marque, coordonnées et textes du garage viennent de l'API ; rien n'est écrit en dur.
 */
export function PortalView({ view }: { view: PortalViewOutput }) {
  const preview = usePortalPreview() !== null;
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const { typing } = useInputMode();
  const tabs = useMemo(() => tabsFor(view.mode), [view.mode]);
  const [state, setState] = useState<{ tab: PortalTab; target: string | null; seq: number }>(() => ({
    tab: preview ? 'summary' : readStoredTab(tabs),
    target: null,
    seq: 0,
  }));
  const tab = tabs.includes(state.tab) ? state.tab : 'summary';

  // Pastille des messages non lus : `unreadMessages` de l'API (lecture rattachée à l'intervention, pas à
  // l'appareil). Onglet Messages ouvert : lecture enregistrée jusqu'au dernier message affiché (idempotente).
  const qc = useQueryClient();
  const lastId = latestMessageId(view.messages);
  const canRead = !preview && view.mode === 'active' && tabs.includes('messages');
  const pendingRead = canRead && tab === 'messages' && view.unreadMessages > 0 && lastId !== null;
  useEffect(() => {
    if (!pendingRead || !lastId) return;
    api('portal.markMessagesRead', { body: { lastMessageId: lastId } })
      .then((out) => qc.setQueryData<PortalViewOutput>(PORTAL_QUERY_KEY, (v) => (v ? { ...v, unreadMessages: out.unreadMessages } : v)))
      .catch(() => {
        // Lecture non enregistrée (réseau, lien expiré) : la pastille reste, sans gêner la lecture.
      });
  }, [pendingRead, lastId, qc]);

  const goTo = useCallback<PortalNav['goTo']>(
    (next, target = null) => {
      if (preview) {
        reveal(target);
        return;
      }
      storeTab(next);
      setState((s) => ({ tab: next, target, seq: s.seq + 1 }));
    },
    [preview],
  );
  const nav = useMemo<PortalNav>(() => ({ goTo }), [goTo]);

  // Après un changement d'onglet : le bloc visé, sinon le haut de l'onglet (jamais au premier affichage).
  useEffect(() => {
    if (state.seq > 0) reveal(state.target);
  }, [state.seq, state.target]);

  const unread = !canRead || tab === 'messages' ? 0 : view.unreadMessages;

  const panels: Record<PortalTab, () => ReactNode> = {
    summary: () => <SummaryTab view={view} unread={unread} />,
    reception: () => <ReceptionTab view={view} />,
    work: () => <WorkTab view={view} />,
    messages: () => <MessagesTab view={view} />,
  };
  const footer = <PrivacyFooter url={view.privacyUrl ?? null} />;

  let body: ReactNode;
  if (preview) {
    body = (
      <div className="bg-canvas pb-10 text-ink">
        <BrandHeader garage={view.garage} />
        <main className="mx-auto max-w-2xl sm:px-4 sm:pt-6">
          {tabs.map((t) => (
            <div key={t} data-portal-tab={t} className="border-b border-dashed border-line pb-8 last:border-b-0">
              <p className="px-4 pt-6 text-xs font-medium tracking-wide text-ink-3 uppercase sm:px-0">Onglet {PORTAL_TAB_LABELS[t]}</p>
              {panels[t]()}
            </div>
          ))}
          {footer}
        </main>
      </div>
    );
  } else {
    const panel = (
      <div role="tabpanel" id="portal-panel" aria-labelledby={`portal-tab-${tab}`} data-portal-tab={tab}>
        {panels[tab]()}
      </div>
    );
    body = desktop ? (
      <div className="flex min-h-dvh bg-canvas text-ink">
        <aside className="sticky top-0 flex h-dvh w-[var(--sidebar-w)] shrink-0 flex-col border-r border-chrome-line bg-chrome text-on-chrome">
          <div className="flex min-h-16 shrink-0 items-center gap-3 px-4 py-3">
            <BrandMark name={view.garage.displayName} logoUrl={view.garage.logoUrl} iconUrl={view.garage.iconUrl ?? null} size="md" />
            <span className="min-w-0 text-base leading-tight font-semibold tracking-[-0.01em] text-on-chrome">{view.garage.displayName}</span>
          </div>
          <div className="px-4 pb-3">
            <VehicleText vehicle={view.vehicle} className="text-on-chrome-2" />
          </div>
          <TabList tabs={tabs} tab={tab} unread={unread} onSelect={goTo} variant="sidebar" />
        </aside>
        <div className="min-w-0 flex-1">
          <main className="mx-auto w-full max-w-2xl px-8 pt-8 pb-16">
            {panel}
            {footer}
          </main>
        </div>
      </div>
    ) : (
      <div className="flex min-h-dvh flex-col bg-canvas text-ink">
        <BrandHeader garage={view.garage} />
        <main className="mx-auto w-full max-w-2xl flex-1 pb-10 sm:px-4 sm:pt-6">
          {panel}
          {footer}
        </main>
        <nav aria-label="Rubriques du suivi" hidden={typing} className="sticky bottom-0 z-20 border-t border-chrome-line bg-chrome/95 pb-[var(--safe-bottom)] text-on-chrome backdrop-blur-md">
          <TabList tabs={tabs} tab={tab} unread={unread} onSelect={goTo} variant="bottom" />
        </nav>
      </div>
    );
  }

  return (
    <TimezoneContext.Provider value={view.intervention.timezone}>
      <PortalNavContext.Provider value={nav}>{body}</PortalNavContext.Provider>
    </TimezoneContext.Provider>
  );
}

const BOTTOM_ITEM =
  'group flex min-h-16 flex-col items-center justify-center gap-1 px-0.5 pt-1.5 pb-1 text-xs leading-none text-on-chrome-3 focus-visible:outline-chrome-focus focus-visible:-outline-offset-4 aria-selected:font-medium aria-selected:text-on-chrome';
const BOTTOM_PILL =
  'relative grid h-8 w-14 place-items-center rounded-full transition-colors duration-150 group-aria-selected:bg-chrome-active-soft group-aria-selected:text-chrome-active-soft-icon';
const SIDEBAR_ITEM =
  "group relative flex min-h-(--ctl-lg) w-full items-center gap-3 rounded-lg px-3 text-left text-base text-on-chrome-2 transition-colors duration-150 before:absolute before:inset-y-2 before:-left-3 before:w-[3px] before:rounded-r-full before:bg-[var(--chrome-indicator)] before:opacity-0 before:content-[''] hover:bg-chrome-hover hover:text-on-chrome focus-visible:outline-chrome-focus aria-selected:bg-chrome-active-soft aria-selected:font-medium aria-selected:text-chrome-active-soft-ink aria-selected:before:opacity-100";

/**
 * Onglets (rôle `tablist`, flèches du clavier). Ce sont des boutons : l'adresse et son fragment (le jeton du
 * client) ne changent jamais. Pastille sur Messages quand le garage a écrit depuis la dernière visite.
 */
function TabList({
  tabs,
  tab,
  unread,
  onSelect,
  variant,
}: {
  tabs: PortalTab[];
  tab: PortalTab;
  unread: number;
  onSelect: (tab: PortalTab) => void;
  variant: 'bottom' | 'sidebar';
}) {
  const refs = useRef(new Map<PortalTab, HTMLButtonElement>());
  const descId = useId();
  const sidebar = variant === 'sidebar';
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.indexOf(tab);
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    let next: PortalTab | undefined;
    if (step) next = tabs[(i + step + tabs.length) % tabs.length];
    else if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs.at(-1);
    if (!next) return;
    e.preventDefault();
    onSelect(next);
    refs.current.get(next)?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label="Rubriques du suivi"
      aria-orientation={sidebar ? 'vertical' : 'horizontal'}
      onKeyDown={onKeyDown}
      className={sidebar ? 'flex flex-1 flex-col gap-1 overflow-y-auto px-3 pt-2 pb-4' : 'mx-auto grid max-w-2xl'}
      style={sidebar ? undefined : { gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      {tabs.map((t) => {
        const Icon = TAB_ICONS[t];
        const selected = t === tab;
        const badge = t === 'messages' && unread > 0 ? unread : 0;
        return (
          <button
            key={t}
            ref={(el) => {
              if (el) refs.current.set(t, el);
              else refs.current.delete(t);
            }}
            type="button"
            role="tab"
            id={`portal-tab-${t}`}
            aria-selected={selected}
            aria-controls="portal-panel"
            tabIndex={selected ? 0 : -1}
            aria-describedby={badge ? `${descId}-unread` : undefined}
            onClick={() => onSelect(t)}
            className={sidebar ? SIDEBAR_ITEM : BOTTOM_ITEM}
          >
            {sidebar ? (
              <>
                <Icon size={18} className="shrink-0 text-on-chrome-3 group-hover:text-on-chrome-2 group-aria-selected:text-chrome-active-soft-icon" />
                {PORTAL_TAB_LABELS[t]}
                {badge ? <UnreadBadge count={badge} className="ml-auto" /> : null}
              </>
            ) : (
              <>
                <span className={BOTTOM_PILL}>
                  <Icon size={22} />
                  {badge ? <UnreadBadge count={badge} className="absolute -top-1 left-[calc(50%+6px)] ring-2 ring-chrome" /> : null}
                </span>
                {PORTAL_TAB_LABELS[t]}
              </>
            )}
            {badge ? (
              <span id={`${descId}-unread`} hidden>
                {badge} nouveau{badge > 1 ? 'x' : ''} message{badge > 1 ? 's' : ''}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function UnreadBadge({ count, className }: { count: number; className?: string }) {
  return (
    <span
      aria-hidden
      data-testid="unread-badge"
      className={cx('grid h-5 min-w-5 place-items-center rounded-full bg-chrome-badge px-1.5 text-2xs leading-none font-semibold text-on-chrome-badge tabular-nums', className)}
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}
